import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { getNotifications } from '../functions/user';
import { useToast } from '../components/ToastContainer';
import { useLocation } from 'react-router-dom';

const isBirthdayNotificationActive = (notification) => {
  if (notification?.type !== 'birthday') return true;
  if (!notification.createdAt) return false;

  const created = new Date(notification.createdAt);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);

  return created >= todayStart && created < todayEnd;
};

const filterActiveNotifications = (notificationsList) => {
  if (!Array.isArray(notificationsList)) return [];
  return notificationsList.filter(isBirthdayNotificationActive);
};

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    const storedValue = window.localStorage.getItem('notificationsEnabled');
    return storedValue !== null ? storedValue === 'true' : true;
  });
  
  const user = useSelector((state) => state.user);
  const { showToast } = useToast();
  const location = useLocation();

  // Reset initial fetch when user changes
  useEffect(() => {
    setInitialFetchDone(false);
  }, [user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncNotificationSetting = (event) => {
      if (event?.type === 'notifications-setting-changed' && typeof event.detail?.enabled === 'boolean') {
        setNotificationsEnabled(event.detail.enabled);
        return;
      }

      const storedValue = window.localStorage.getItem('notificationsEnabled');
      setNotificationsEnabled(storedValue !== null ? storedValue === 'true' : true);
    };

    window.addEventListener('storage', syncNotificationSetting);
    window.addEventListener('notifications-setting-changed', syncNotificationSetting);

    return () => {
      window.removeEventListener('storage', syncNotificationSetting);
      window.removeEventListener('notifications-setting-changed', syncNotificationSetting);
    };
  }, []);

  useEffect(() => {
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      setInitialFetchDone(true);
      setIsConnected(false);
      return;
    }

    setInitialFetchDone(false);
  }, [notificationsEnabled, user?.id]);

  // Fetch initial notifications when user is available
  useEffect(() => {
    const fetchInitialNotifications = async () => {
      if (!notificationsEnabled) return;
      if (user?.token && user?.id && !initialFetchDone) {
        try {
          // console.log('📥 Fetching initial notifications...');
          const notifs = await getNotifications(user.token);
          
          // Handle error message response
          if (typeof notifs === 'string') {
            console.error('❌ Error fetching notifications:', notifs);
            setInitialFetchDone(true);
            return;
          }
          
          if (Array.isArray(notifs)) {
            const activeNotifications = filterActiveNotifications(notifs);
            setNotifications(activeNotifications);
            const unreadNotifications = activeNotifications.filter(notif => !notif.isRead);
            setUnreadCount(unreadNotifications.length);
            console.log(`✅ Loaded ${activeNotifications.length} initial notifications, ${unreadNotifications.length} unread`);
          } else {
            console.warn('⚠️ Unexpected notification format on initial fetch:', typeof notifs);
            setNotifications([]);
            setUnreadCount(0);
          }
          
          setInitialFetchDone(true);
        } catch (error) {
          console.error('❌ Error fetching initial notifications:', error);
          setInitialFetchDone(true); // Mark as done even on error to prevent infinite retries
        }
      }
    };
    
    fetchInitialNotifications();
  }, [user?.token, user?.id, initialFetchDone, notificationsEnabled]);

  // Shared refresh logic: fetch from API and merge into state (no full page reload)
  const refetchNotifications = useCallback(async () => {
    if (!notificationsEnabled) return;
    if (!user?.token) return;
    if (typeof window !== 'undefined' && window.__messengerChatOpen) return; // Do not refresh while messenger chat is open
    try {
      const notifs = await getNotifications(user.token);
      if (!notifs) return;
      if (typeof notifs === 'string') {
        console.error('❌ Error from server:', notifs);
        return;
      }
      if (!Array.isArray(notifs)) return;
      const activeNotifications = filterActiveNotifications(notifs);
      // Server list is source of truth so deleted-post notifications disappear immediately
      setNotifications(prev => {
        const existingMap = new Map();
        prev.forEach(notif => {
          const id = typeof notif._id === 'string' ? notif._id : notif._id.toString();
          existingMap.set(id, notif);
        });
        return activeNotifications.map(notif => {
          const id = typeof notif._id === 'string' ? notif._id : notif._id.toString();
          const existing = existingMap.get(id);
          if (existing) {
            return {
              ...existing,
              ...notif,
              friendRequestStatus: notif.friendRequestStatus || existing.friendRequestStatus
            };
          }
          return notif;
        });
      });
      const unreadNotifications = activeNotifications.filter(notif => !notif.isRead);
      setUnreadCount(unreadNotifications.length);
    } catch (error) {
      console.error('❌ Error refetching notifications:', error);
    }
  }, [user?.token, notificationsEnabled]);

  // Auto-refresh notifications every 30 seconds via API polling (no full page reload)
  useEffect(() => {
    if (!notificationsEnabled) return;
    if (!user?.token || !user?.id) return;
    refetchNotifications();
    const intervalId = setInterval(refetchNotifications, 30000);
    return () => clearInterval(intervalId);
  }, [user?.token, user?.id, refetchNotifications, notificationsEnabled]);

  // Initialize socket connection
  useEffect(() => {
    if (!notificationsEnabled) {
      if (socket) {
        socket.close();
        window.notificationSocket = null;
        setSocket(null);
      }
      return;
    }

    if (user?.id) {
      // Auto-detect backend URL: use env var if set, otherwise detect from current location
      let backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl || backendUrl === 'http://localhost:8000') {
        // In production, use same origin (relative URL) or detect from window
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          
          // Check if it's a production domain (not localhost)
          // This works for usmaapp.com, socialmedia.testatozas.in, and any other production domains
          if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
            // Production: use same origin for socket (socket.io will handle it automatically)
            // Empty string means same origin - socket.io will use current window.location
            backendUrl = '';
            // console.log(`🌐 Production domain detected: ${hostname}, using same origin for socket`);
          } else {
            // Development: use localhost:8000
            backendUrl = 'http://localhost:8000';
            // console.log(`💻 Development mode: using ${backendUrl} for socket`);
          }
        } else {
          backendUrl = 'http://localhost:8000';
        }
      } else {
        console.log(`🔧 Using configured REACT_APP_BACKEND_URL: ${backendUrl}`);
      }
      
      // console.log('🔌 Connecting to notification socket:', backendUrl || `same origin (${window.location.origin})`);
      
      const newSocket = io(backendUrl || undefined, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: false,
        reconnection: true,
        reconnectionAttempts: Infinity, // Keep trying to reconnect
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      newSocket.on('connect', () => {
        // console.log('✅ Connected to notification server');
        setIsConnected(true);
        // Join user-specific room - ensure user.id is a string
        if (user?.id) {
          const userId = String(user.id);
          newSocket.emit('join', userId);
          // console.log(`✅ Joined notification room: user_${userId}`);
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ Disconnected from notification server:', reason);
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Notification socket connection error:', error);
        setIsConnected(false);
      });

      newSocket.on('error', (error) => {
        console.error('❌ Notification socket error:', error);
      });

      // Handle reconnection - rejoin room
      newSocket.on('reconnect', (attemptNumber) => {
        console.log('Reconnected to server after', attemptNumber, 'attempts');
        setIsConnected(true);
        // Rejoin user-specific room after reconnection
        if (user?.id) {
          const userId = String(user.id);
          newSocket.emit('join', userId);
          console.log(`Rejoined notification room for user ${userId} after reconnect`);
        }
      });

      // Listen for new notifications
      newSocket.on('newNotification', (notification) => {
        if (!notificationsEnabled) return;
        if (!isBirthdayNotificationActive(notification)) return;
        console.log('🔔 New notification received via socket:', notification);
        setNotifications(prev => {
          // Check if notification already exists to prevent duplicates
          const notificationId = typeof notification._id === 'string' ? notification._id : notification._id.toString();
          const exists = prev.some(notif => {
            const notifId = typeof notif._id === 'string' ? notif._id : notif._id.toString();
            return notifId === notificationId;
          });
          
          // Only add if it doesn't already exist
          if (!exists) {
            // Update unread count if notification is unread
            if (!notification.isRead) {
              setUnreadCount(prevCount => prevCount + 1);
            }
            
            // Show beautiful toast notification
            showNotificationToast(notification);
            
            // Show browser notification if permission is granted
            if (Notification.permission === 'granted') {
              new Notification(notification.message, {
                icon: notification.from?.picture || '/images/default_pic.png',
                body: notification.postText || '',
                tag: notification._id
              });
            }
            
            return [notification, ...prev];
          } else {
            console.log('Notification already exists, skipping duplicate');
            return prev;
          }
        });
      });

      // Listen for updated notifications
      newSocket.on('updatedNotification', (notification) => {
        if (!notificationsEnabled) return;
        // console.log('🔄 Notification updated via socket:', notification);
        setNotifications(prev => {
          let wasUnread = false;
          let notificationExists = false;
          
          const updated = prev.map(notif => {
            // Handle both string and ObjectId comparisons
            const notifId = typeof notif._id === 'string' ? notif._id : notif._id.toString();
            const updatedId = typeof notification._id === 'string' ? notification._id : notification._id.toString();
            if (notifId === updatedId) {
              notificationExists = true;
              // Track if notification was previously unread
              wasUnread = !notif.isRead;
              // Merge the updated notification with existing one to preserve any local state
              // Ensure friendRequestStatus and other fields are properly updated
              return { ...notif, ...notification };
            }
            return notif;
          });
          
          // If notification doesn't exist in the list yet, add it
          // This can happen if the update arrives before the initial notification
          if (!notificationExists) {
            console.log('Updated notification not found in list, adding it:', notification);
            if (!notification.isRead) {
              setUnreadCount(prevCount => prevCount + 1);
            }
            return [notification, ...prev];
          }
          
          // Update unread count based on the change
          if (notification.isRead && wasUnread) {
            // Notification was unread but is now read - decrement
            setUnreadCount(prevCount => Math.max(0, prevCount - 1));
          } else if (!notification.isRead && !wasUnread) {
            // Notification was read but is now unread - increment
            setUnreadCount(prevCount => prevCount + 1);
          }
          
          return updated;
        });
      });
      
      // Listen for live stream started events
      newSocket.on('liveStreamStarted', (data) => {
        if (!notificationsEnabled) return;
        console.log('Live stream started:', data);
        
        // Show toast notification for live stream
        showToast({
          type: 'info',
          title: '🔴 Live Stream Started!',
          message: `${data.streamer.first_name} ${data.streamer.last_name} is going live: ${data.title}`,
          avatar: data.streamer.picture,
          duration: 6000,
          position: 'top-right',
          actions: (
            <button 
              className="btn-primary"
              onClick={() => {
                window.location.href = `/notifications?liveStreamId=${data.streamId}`;
              }}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                background: '#e74c3c',
                color: 'white'
              }}
            >
              Watch Live
            </button>
          )
        });
        
        // Show browser notification for live streams
        if (Notification.permission === 'granted') {
          new Notification(`${data.streamer.first_name} ${data.streamer.last_name} is going live!`, {
            body: data.title,
            icon: data.streamer.picture || '/images/default_pic.png',
            tag: `live_${data.streamId}`
          });
        }
      });

      // Listen for deleted notifications (e.g. when a related post is deleted)
      newSocket.on('deletedNotification', (notificationId) => {
        if (!notificationsEnabled) return;
        const deletedId = notificationId != null ? String(notificationId) : '';
        if (!deletedId) return;
        setNotifications(prev => {
          const deletedNotification = prev.find(notif => String(notif._id) === deletedId);
          if (deletedNotification && !deletedNotification.isRead) {
            setUnreadCount(prevCount => Math.max(0, prevCount - 1));
          }
          return prev.filter(notif => String(notif._id) !== deletedId);
        });
      });

      setSocket(newSocket);
      
      // Make socket available globally for other contexts
      window.notificationSocket = newSocket;

      // Cleanup on unmount
      return () => {
        newSocket.close();
        window.notificationSocket = null;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, showToast, notificationsEnabled]);

  // Function to show notification toast
  const showNotificationToast = useCallback((notification) => {
    if (!notificationsEnabled) return;
    const isOnNotificationsPage = location.pathname === '/notifications';
    
    // Don't show toast if user is already on notifications page
    if (isOnNotificationsPage) return;

    let toastType = 'info';
    let title = '';
    let actions = null;

    switch (notification.type) {
      case 'like':
        toastType = 'success';
        title = '👍 New Like';
        break;
      case 'comment':
        toastType = 'info';
        title = '💬 New Comment';
        break;
      case 'comment_reply':
        toastType = 'info';
        title = '↩️ Comment Reply';
        break;
      case 'friend_request':
        toastType = 'info';
        title = '👤 Friend Request';
        actions = (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="btn-primary"
              onClick={() => {
                window.location.href = '/friends';
              }}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                background: '#42b883',
                color: 'white'
              }}
            >
              View Request
            </button>
          </div>
        );
        break;
      case 'live_stream':
        toastType = 'info';
        title = '🔴 Live Stream Started!';
        actions = (
          <button 
            className="btn-primary"
            onClick={() => {
              if (notification.liveStreamId) {
                window.location.href = `/notifications?liveStreamId=${notification.liveStreamId}`;
              } else {
                window.location.href = '/notifications';
              }
            }}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              background: '#e74c3c',
              color: 'white'
            }}
          >
            Watch Live
          </button>
        );
        break;
      default:
        title = '🔔 Notification';
    }

    showToast({
      type: toastType,
      title,
      message: notification.message,
      avatar: notification.from?.picture,
      duration: 4000,
      position: 'top-right',
      actions: actions || (
        <button 
          className="btn-secondary"
          onClick={() => {
            if (notification.postId) {
              window.location.href = `/post/${notification.postId}`;
            } else {
              window.location.href = '/notifications';
            }
          }}
          style={{
            padding: '6px 12px',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            background: '#f0f2f5',
            color: '#424245'
          }}
        >
          View
        </button>
      )
    });
  }, [location.pathname, showToast, notificationsEnabled]);

  // Request notification permission on mount
  useEffect(() => {
    if (!notificationsEnabled) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [notificationsEnabled]);

  // Memoized function to mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif._id === notificationId 
          ? { ...notif, isRead: true }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Memoized function to mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, isRead: true }))
    );
    setUnreadCount(0);
  }, []);

  // Memoized function to remove notification
  const removeNotification = useCallback((notificationId) => {
    const targetId = notificationId != null ? String(notificationId) : '';
    if (!targetId) return;
    setNotifications(prev => {
      const removedNotification = prev.find(notif => String(notif._id) === targetId);
      if (removedNotification && !removedNotification.isRead) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
      return prev.filter(notif => String(notif._id) !== targetId);
    });
  }, []);

  // Memoized function to clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Memoized function to update a specific notification
  const updateNotification = useCallback((notificationId, updateData) => {
    setNotifications(prev => 
      prev.map(notif => {
        const notifId = typeof notif._id === 'string' ? notif._id : notif._id.toString();
        const targetId = typeof notificationId === 'string' ? notificationId : notificationId.toString();
        if (notifId === targetId) {
          return { ...notif, ...updateData };
        }
        return notif;
      })
    );
  }, []);

  // Memoized function to set initial notifications (from API) - for external use
  const setInitialNotifications = useCallback((notificationsList) => {
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    // This function is now mainly for external components, 
    // but we handle initial fetch internally
    if (notificationsList && notificationsList.length > 0) {
      const activeNotifications = filterActiveNotifications(notificationsList);
      setNotifications(activeNotifications);
      const unreadNotifications = activeNotifications.filter(notif => !notif.isRead);
      setUnreadCount(unreadNotifications.length);
    }
  }, [notificationsEnabled]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    socket,
    notifications,
    unreadCount,
    isConnected,
    notificationsEnabled,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    updateNotification,
    setInitialNotifications,
    refetchNotifications
  }), [
    socket,
    notifications,
    unreadCount,
    isConnected,
    notificationsEnabled,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    updateNotification,
    setInitialNotifications,
    refetchNotifications
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 